import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

import { fetchApInvoiceDocumentSpreadsheetPreview } from "./apInvoicesApi";

export function ApInvoiceSpreadsheetPreview({
  invoiceId,
  documentId,
  fillAvailableHeight = false,
  frameClassName,
}: {
  invoiceId: string;
  documentId: string;
  fillAvailableHeight?: boolean;
  frameClassName: string;
}): JSX.Element {
  const [sheetIndex, setSheetIndex] = useState(0);

  useEffect(() => {
    setSheetIndex(0);
  }, [documentId]);

  const spreadsheetQuery = useQuery({
    queryKey: ["accounting", "ap-invoice", invoiceId, "spreadsheet-preview", documentId, sheetIndex],
    queryFn: () => fetchApInvoiceDocumentSpreadsheetPreview(invoiceId, documentId, sheetIndex),
    staleTime: 120_000,
    gcTime: 120_000,
  });

  if (spreadsheetQuery.isPending) {
    return (
      <div className={cn("flex items-center justify-center text-sm text-slate-500", frameClassName)}>
        Loading workbook…
      </div>
    );
  }

  if (spreadsheetQuery.isError) {
    return (
      <div className={cn("flex items-center justify-center px-4 text-sm text-rose-700", frameClassName)}>
        {(spreadsheetQuery.error as Error).message ?? "Could not load workbook preview."}
      </div>
    );
  }

  const preview = spreadsheetQuery.data;
  const showSheetPicker = preview.sheets.length > 1;

  return (
    <div className={cn("flex min-h-0 flex-col gap-2", fillAvailableHeight && "flex-1")}>
      {showSheetPicker ? (
        <div className="flex shrink-0 flex-wrap gap-2 px-2 pt-2">
          {preview.sheets.map((sheet) => {
            const active = sheet.index === sheetIndex;
            return (
              <button
                key={sheet.index}
                type="button"
                onClick={() => setSheetIndex(sheet.index)}
                className={cn(
                  "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                  active
                    ? "border-slate-800 bg-slate-800 text-white"
                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
                )}
              >
                {sheet.name}
              </button>
            );
          })}
        </div>
      ) : null}
      <div className={cn("overflow-auto rounded-md border border-slate-200 bg-white", frameClassName)}>
        <table className="min-w-full border-collapse text-left text-xs text-slate-800">
          <thead className="sticky top-0 z-10 bg-slate-100 shadow-[0_1px_0_0_rgb(226_232_240)]">
            <tr>
              {preview.sheet.columns.map((column) => (
                <th
                  key={column.key}
                  className="whitespace-nowrap border-b border-slate-200 px-2 py-1.5 font-semibold text-slate-700"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.sheet.rows.length === 0 ? (
              <tr>
                <td
                  colSpan={Math.max(preview.sheet.columns.length, 1)}
                  className="px-2 py-8 text-center text-sm text-slate-500"
                >
                  No rows to show on this sheet.
                </td>
              </tr>
            ) : (
              preview.sheet.rows.map((row, rowIndex) => (
                <tr key={`${preview.sheet.index}-${rowIndex}`} className="odd:bg-white even:bg-slate-50/70">
                  {row.map((value, columnIndex) => (
                    <td
                      key={`${preview.sheet.columns[columnIndex]?.key ?? columnIndex}-${rowIndex}`}
                      className="whitespace-pre-wrap border-b border-slate-100 px-2 py-1 align-top"
                    >
                      {value}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
        {preview.truncated_rows || preview.truncated_columns ? (
          <p className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500">
            Preview is truncated. Download the file to see the full workbook.
          </p>
        ) : null}
      </div>
    </div>
  );
}
